import { Trans } from 'react-i18next';
import { useSnackbar } from 'notistack';
import _, { debounce, isString } from 'lodash';
import { useRef, Fragment, useState } from 'react';

import { LoadingButton } from '@mui/lab';
import Dialog from '@mui/material/Dialog';
import Grid from '@mui/material/Unstable_Grid2';
import DialogTitle from '@mui/material/DialogTitle';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import {
  Box,
  Chip,
  List,
  Alert,
  Stack,
  Button,
  Divider,
  Checkbox,
  ListItem,
  TextField,
  Typography,
  Autocomplete,
} from '@mui/material';

import { useBoolean } from 'src/hooks/use-boolean';

import i18n from 'src/locales/i18n';
import { useTranslate } from 'src/locales';
import interview from 'src/assets/lottie/interview.json';
import { DocumentType } from 'src/services/files/file.types';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import { SubmissionData } from 'src/services/applications/applications.types';
import { useGetMyProfileQuery } from 'src/services/candidates/candidates.service';
import { hideCandidateJobInviteActionModal } from 'src/store/slices/jobs/jobsSlice';
import { ICandidateProfileDocument } from 'src/services/candidates/candidates.types';
import {
  IJob,
  LinkBehaviour,
  JobInviteAction,
  JobVerificationStatus,
} from 'src/services/jobs/jobs.types';
import {
  useActionJobInviteMutation,
  useGetOrganisationJobsQuery,
  useGetComplianceQuestionsByJobQuery
} from 'src/services/jobs/jobs.service';

import Iconify from 'src/components/iconify';
import AnimationContainer from 'src/components/animation/container';
import SelectableCard from 'src/components/selectable-card/selectable-card';
import ScreeningQuestionsForm, { ScreeningFormHandle } from 'src/components/compliance/screening-questions-form';

import { ProfileDocumentSelector } from 'src/sections/profile/documents/profile-document-selector';

import { BusinessErrorCodes } from 'src/types/business-errors';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

type Props = {
  open: boolean;
  // Disable the clone or link functionality
  // the job will automatically be cloned when accepted.
  disableCloneOrLink?: boolean;
};

enum ModalView {
  REQUEST = 'REQUEST',
  DOCUMENTS = 'DOCUMENTS',
  COMPLIANCE = 'COMPLIANCE',
  ACTION = 'ACTION'
}

const generateJobInviteAcceptError = (e: any): string => {
  if (isString(e?.data?.error_code)) {
    if (e?.data.error_code === BusinessErrorCodes.UnableToActionInvite) {
      return i18n.t('jobs.invite_modal.api.accept.error.invalid');
    }

    if (e?.data.error_code === BusinessErrorCodes.CandidateAlreadyApplied) {
      return i18n.t('jobs.invite_modal.api.accept.error.exists');
    }
  }

  return i18n.t('jobs.invite_modal.api.accept.error.default');
};

const generateJobInviteRejectError = (e: any): string => {
  if (isString(e?.data?.error_code)) {
    if (e?.data.error_code === BusinessErrorCodes.UnableToActionInvite) {
      return i18n.t('jobs.invite_modal.api.reject.error.invalid');
    }
  }

  return i18n.t('jobs.invite_modal.api.reject.error.default');
};

export default function ActionJobInviteModal({ open, disableCloneOrLink = false }: Props) {
  const { t } = useTranslate();

  const { enqueueSnackbar } = useSnackbar();

  const addDocumentsControl = useBoolean();

  const complianceFormRef = useRef<ScreeningFormHandle>(null);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [resume, setResume] = useState<ICandidateProfileDocument | null>(null);
  const [selectedDocument, setSelectedDocument] = useState<ICandidateProfileDocument | null>(null);
  const [additionalDocuments, setAdditionalDocuments] = useState<ICandidateProfileDocument[]>([]);

  const [complianceSubmissions, setComplianceSubmissions] = useState<SubmissionData[]>([]);
  
  const [documentSelectError, setDocumentSelectError] = useState<string | null>(null);

  const [currentView, setCurrentView] = useState<ModalView>(ModalView.REQUEST);

  const [linkBehaviour, setLinkBehaviour] = useState<LinkBehaviour>(LinkBehaviour.CLONE_SOURCE);

  const [selectedJob, onSelectJob] = useState<IJob>();

  const dispatch = useAppDispatch();

  const [jobSearchQuery, setJobSearchQuery] = useState('');

  const candidateJobInvite = useAppSelector((state) => state.jobs.candidateJobInvite);

  const { currentData: jobsData } = useGetOrganisationJobsQuery({
    search_query: jobSearchQuery,
    page: 1,
    per_page: 5,
    linked_application: false,
  });

  const { currentData: jobQuestionsData } = useGetComplianceQuestionsByJobQuery({
    jobId: candidateJobInvite?.job.id as string,
  }, {
    skip: !candidateJobInvite,
  });

  const { currentData: myProfile } = useGetMyProfileQuery();

  const [actionRequest, { isLoading: isSubmitting }] = useActionJobInviteMutation();

  const onClose = (e: any = null, reason: string | null = null) => {

    if (reason && reason === 'backdropClick') return;

    setCurrentView(ModalView.REQUEST);
    dispatch(hideCandidateJobInviteActionModal());
  };

  const onNextPage = () => {
    setErrorMessage(null);

    if (currentView === ModalView.REQUEST) {
      setCurrentView(ModalView.DOCUMENTS);
    }
    else if (currentView === ModalView.DOCUMENTS) {

      if (!resume) {
        setErrorMessage(t('jobs.invite_modal.resume_required_error'));
        return;
      }

      if (jobQuestionsData?.length) {
        setCurrentView(ModalView.COMPLIANCE);
      }
      else {
        if (disableCloneOrLink) {
          onAcceptRequest();
          return;
        }

        setCurrentView(ModalView.ACTION);
      }
    }
    else if (currentView === ModalView.COMPLIANCE) {
      onCompleteComplianceSubmission();
    }
  };

  const onRejectRequest = async () => {
    setErrorMessage(null);

    if (!candidateJobInvite) return;

    try {
      await actionRequest({
        requestId: candidateJobInvite.id,
        data: {
          action: JobInviteAction.REJECT,
        },
      }).unwrap();

      onClose();

      enqueueSnackbar(t('jobs.invite_modal.api.reject.success'));
    } catch (e) {
      console.error(e);

      enqueueSnackbar(generateJobInviteRejectError(e), { variant: 'error' });
    }
  };

  const onCompleteComplianceSubmission = async () => {
    if (!complianceFormRef.current) return;

    const isValid = await complianceFormRef.current.validate();

    if (!isValid) {
      return;
    }

    const submissions = await complianceFormRef.current.getSubmissions();
    setComplianceSubmissions(submissions);

    if (disableCloneOrLink) {
      onAcceptRequest(submissions);
      return;
    }

    setCurrentView(ModalView.ACTION);
  }

  // NOTE: we used direct submissions because ref'd compliance form is not available in the action view (out of DOM; cannot read data)
  const onAcceptRequest = async (directSubmissions?: SubmissionData[]) => {
    if (!candidateJobInvite) return;

    const isLinkExisting = linkBehaviour === LinkBehaviour.LINK_EXISTING;

    if (!resume) {
      setErrorMessage(t('jobs.invite_modal.resume_required_error'));
      return;
    }

    setErrorMessage(null);

    try {

      await actionRequest({
        requestId: candidateJobInvite.id,
        data: {
          action: JobInviteAction.ACCEPT,
          link_settings: {
            job_id: selectedJob && isLinkExisting ? selectedJob.id : undefined,
            link_behaviour: linkBehaviour,
          },
          compliance_submissions: directSubmissions || complianceSubmissions,
          documents: {
            resume_id: resume?.id as string,
            additional_documents: additionalDocuments.map((doc) => doc.id),
          }
        },
      }).unwrap();

      enqueueSnackbar(
        t(`jobs.invite_modal.api.accept.success.${isLinkExisting ? 'link' : 'clone'}`, {
          role: selectedJob?.title,
        })
      );

      setErrorMessage(
        t(`jobs.invite_modal.api.accept.success.${isLinkExisting ? 'link' : 'clone'}`, {
          role: selectedJob?.title,
        })
      );

      onClose();
    } catch (e) {
      console.error(e);
      enqueueSnackbar(generateJobInviteAcceptError(e), { variant: 'error' });
    }
  };

  const onSearchJobs = debounce((input: string) => {
    setJobSearchQuery(input);
  }, 500);

  const onResumeSelected = (doc: ICandidateProfileDocument) => {
    setResume(doc);
  };

  const onDocumentSelected = (doc: ICandidateProfileDocument) => {
    setSelectedDocument(doc as ICandidateProfileDocument);
  };

  const onDocumentAttached = () => {
    if (!selectedDocument) {
      setDocumentSelectError(t('jobs.invite_modal.documents_select_error'));
      return;
    };

    // add selected document to the list of additional documents
    setAdditionalDocuments([...additionalDocuments, selectedDocument]);
    setSelectedDocument(null);
    setDocumentSelectError(null);
    addDocumentsControl.onFalse();
  };

  const onDocumentRemoved = (doc: ICandidateProfileDocument) => {
    // remove selected document from the list of additional documents
    setAdditionalDocuments(additionalDocuments.filter((item) => item.id !== doc.id));
  };

  const onCloseDocumentSelector = () => {
    setSelectedDocument(null);
    setDocumentSelectError(null);
    addDocumentsControl.onFalse();
  }

  const renderRequestView = (
    <Grid container gap={2}>
      <Grid xs={12}>
        <Box>
          <AnimationContainer height={250} json={interview} loop />
        </Box>
      </Grid>

      <Grid xs={12} rowGap={1}>
        <Grid xs={12}>
          <Typography textAlign="center">
            <Trans>
              {t('jobs.invite_modal.alert', {
                role: candidateJobInvite?.job.title,
                company: candidateJobInvite?.initiator.company_name,
              })}
            </Trans>
          </Typography>
        </Grid>

        <Grid xs={12} flexDirection="row" spacing={2} py={2}>
          <Stack direction="row" gap={2}>
            <LoadingButton
              fullWidth
              loading={isSubmitting}
              variant="outlined"
              onClick={onRejectRequest}
            >
              {t('common.decline')}
            </LoadingButton>

            <LoadingButton
              onClick={onNextPage}
              loading={isSubmitting}
              variant="contained"
              color="primary"
              fullWidth
            >
              {t('common.accept')}
            </LoadingButton>
          </Stack>
        </Grid>
      </Grid>
    </Grid>
  );

  const renderComplianceView = (
    <Stack direction="column">
      <Grid container gap={2}>
        <Grid xs={12}>
          <Typography variant="h6">{t('jobs.invite_modal.compliance_title')}</Typography>
        </Grid>
        <Grid xs={12}>
          <ScreeningQuestionsForm questions={jobQuestionsData ?? []} ref={complianceFormRef} />
        </Grid>
      </Grid>
    </Stack>
  )

  const renderActionView = (
    <Stack direction="column">
      <Grid container gap={2}>
        <Grid xs={12}>
          <SelectableCard
            title={t('jobs.action.select_job.clone')}
            subtitle={t('jobs.action.select_job.clone_info')}
            selected={linkBehaviour === LinkBehaviour.CLONE_SOURCE}
            onClick={() => setLinkBehaviour(LinkBehaviour.CLONE_SOURCE)}
            flexDirection="row"
          />
        </Grid>
        <Grid xs={12}>
          <SelectableCard
            title={t('jobs.action.select_job.select')}
            subtitle={t('jobs.invite_modal.clone')}
            selected={linkBehaviour === LinkBehaviour.LINK_EXISTING}
            onClick={() => setLinkBehaviour(LinkBehaviour.LINK_EXISTING)}
            flexDirection={linkBehaviour === LinkBehaviour.LINK_EXISTING ? 'column' : 'row'}
          >
            {linkBehaviour === LinkBehaviour.LINK_EXISTING && (
              <Stack sx={{ pt: 2 }}>
                <Autocomplete
                  id="job-select"
                  onChange={(_e: any, value) => value && onSelectJob(value)}
                  options={jobsData?.results ?? []}
                  isOptionEqualToValue={(option: IJob, value: IJob) => option.id === value.id}
                  getOptionLabel={(option) => option.title}
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox
                        icon={icon}
                        checkedIcon={checkedIcon}
                        style={{ marginRight: 8 }}
                        checked={selected}
                        disabled={option.verification_status !== JobVerificationStatus.UNVERIFIED}
                      />
                      {option.title}
                    </li>
                  )}
                  style={{ width: 500 }}
                  renderInput={(params) => (
                    <TextField {...params} label={t('common.jobs')} placeholder="Favorites" />
                  )}
                  onInputChange={(_e: any, value: string) => onSearchJobs(value)}
                />
              </Stack>
            )}
          </SelectableCard>
        </Grid>
      </Grid>
    </Stack>
  );

  const renderDocumentsView = (
    <Stack direction="column">
      <Grid container gap={2}>
        <Grid xs={12}>
          <Stack direction="column" width="100%" >
            <Typography variant="h6">{t('jobs.invite_modal.documents_title')}</Typography>
            <Typography variant="body2">{t('jobs.invite_modal.documents_subtitle')}</Typography>
          </Stack>
        </Grid>
        <Grid xs={12}>
          <Stack direction="column" width="100%" gap={1}>
            <Typography variant="subtitle2">
              {t('jobs.invite_modal.labels.resume')}
            </Typography>
            <ProfileDocumentSelector
              enableUpload
              type={DocumentType.RESUME}
              onSelect={(type, selectedDoc) => onResumeSelected(selectedDoc as ICandidateProfileDocument)}
              profileId={myProfile?.id as string}
            />
          </Stack>
        </Grid>
        <Grid xs={12}>
          <Stack direction="column" width="100%" gap={1}>
            <Stack direction="row" width="100%" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle2">
                {t('jobs.invite_modal.labels.additional_documents')}
              </Typography>
              {
                additionalDocuments.length < 5 && (
                  <Iconify
                    icon="solar:add-circle-bold"
                    color="primary.main"
                    onClick={addDocumentsControl.onTrue}
                    sx={{
                      width: 20,
                      height: 20,
                      cursor: 'pointer',
                    }}
                  />
                )
              }

            </Stack>

            {/* list of additional documents */}

            {additionalDocuments.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('jobs.invite_modal.documents_empty')}
                </Typography>
              </Box>
            )}


            <Box>
              <List>
                {additionalDocuments.map((doc, index) => (
                  <Fragment key={doc.id}>
                    <ListItem disablePadding key={index}>
                      <Stack direction="row" width="100%" gap={1} key={index} alignItems="center" justifyContent="space-between">
                        <Typography variant="body2" sx={{ flexGrow: 1 }}>
                          {_.capitalize(doc.document.name)}
                        </Typography>
                        <Stack direction="row" gap={1} alignItems="center">
                          <Chip
                            label={t(`enums.document_type.${doc.document.type}`)}
                            size="small"
                            variant='outlined'
                            color="info"
                          />
                          <Iconify
                            icon="solar:trash-bin-2-bold"
                            color="error.main"
                            sx={{
                              width: 20,
                              height: 20,
                              cursor: 'pointer',
                            }}
                            onClick={() => onDocumentRemoved(doc)}
                          />
                        </Stack>
                      </Stack>
                    </ListItem>
                    <Divider sx={{ my: 1 }} />
                  </Fragment>
                ))}
              </List>
            </Box>
          </Stack>
        </Grid>
      </Grid>

      {/* add documents modal */}

      {
        addDocumentsControl.value && (
          <Dialog fullWidth maxWidth="sm" open={addDocumentsControl.value} onClose={addDocumentsControl.onFalse}>
            <DialogTitle>{t('jobs.invite_modal.add_documents')}</DialogTitle>
            <DialogContent>
              <Box sx={{ mt: 1, mb: 2 }}>
                <ProfileDocumentSelector
                  enableUpload
                  type={DocumentType.RESUME}
                  onSelect={(type, selectedDoc) => onDocumentSelected(selectedDoc as ICandidateProfileDocument)}
                  profileId={myProfile?.id as string}
                />
              </Box>
              <Box>
                {
                  documentSelectError && (
                    <Typography variant="body2" color="error.main" sx={{ mb: 1 }}>
                      {documentSelectError}
                    </Typography>
                  )
                }
              </Box>

            </DialogContent>
            <DialogActions>
              <Button
                color='primary'
                variant="contained"
                onClick={onDocumentAttached}
                startIcon={<Iconify icon="solar:plus-bold" />}
              >
                {t('common.add')}
              </Button>
              <Button onClick={onCloseDocumentSelector}>{t('common.cancel')}</Button>
            </DialogActions>
          </Dialog>
        )

      }

    </Stack>
  );

  const renderCurrentPage = () => {
    if (currentView === ModalView.REQUEST) {
      return renderRequestView;
    }

    if (currentView === ModalView.COMPLIANCE) {
      return renderComplianceView;
    }

    if (currentView === ModalView.DOCUMENTS) {
      return renderDocumentsView;
    }

    return renderActionView;
  };

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={onClose}>
      <DialogTitle sx={{ textTransform: 'capitalize' }}>{t(`jobs.invite_modal.title`)}</DialogTitle>

      <DialogContent>
        {errorMessage && (
          <Alert variant="outlined" severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}

        {renderCurrentPage()}
      </DialogContent>

      <DialogActions>

        <Button onClick={onClose}>{t('common.cancel')}</Button>

        {currentView === ModalView.COMPLIANCE && (
          <LoadingButton
            onClick={onCompleteComplianceSubmission}
            variant="contained"
            loading={isSubmitting}
            disabled={isSubmitting}
          >
            {t('common.next')}
          </LoadingButton>
        )}

        {currentView === ModalView.DOCUMENTS && (
          <LoadingButton
            onClick={onNextPage}
            variant="contained"
            loading={isSubmitting}
            disabled={isSubmitting}
          >
            {t('common.next')}
          </LoadingButton>
        )}


        {currentView === ModalView.ACTION && (
          <LoadingButton
            onClick={() => onAcceptRequest()}
            variant="contained"
            loading={isSubmitting}
            disabled={isSubmitting}
          >
            {t('jobs.action.link_job')}
          </LoadingButton>
        )}

      </DialogActions>
    </Dialog>
  );
}